import { useState, useEffect, useContext } from "react";
import { Button, Flex, Modal, Typography, App, Tag, Pagination } from "antd";
import PlusIcon from "@/icons/PlusIcon";

import UnitTypesTable from "@/components/tables/UnitTypesTable";
import UnitTypeModal from "@/components/modals/UnitTypeModal";
import Breadcrumbs from "@/components/ui/Breadcrumbs";

import { UserContext } from "@/context/UserContext";

import { useTableAutoHeight } from "@/hooks/useTableAutoHeight";

import { getList, deleteItem } from "@/api/unitTypes";

const LOCAL_STORAGE_KEY = "unitTypesTableParams";

const { Title, Text } = Typography;

// Страница типов РГ
// Содержит список типов рабочих групп с возможностью добавления, редактирования и удаления
const UnitTypes = () => {
  const { message } = App.useApp();
  const { user } = useContext(UserContext);

  // Данные
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Инициализация пагинации из localStorage
  const initialTableParams = JSON.parse(
    localStorage.getItem(`${user.user_uuid}_${LOCAL_STORAGE_KEY}`)
  ) || {
    pagination: {
      current: 1,
      pageSize: 10,
      placement: ["bottomLeft"],
      showSizeChanger: true,
    },
  };

  const [tableParams, setTableParams] = useState(initialTableParams);

  // Автоматическая высота таблицы
  const { tableContainerRef, tableHeight, headerHeight } = useTableAutoHeight([
    data,
    tableParams.pagination.pageSize,
  ]);

  // Окно создания/редактирования
  const [isModalShow, setIsModalShow] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  // Запрос на сервер для получения данных
  const fetchData = async (pagination, sorter) => {
    setIsLoading(true);

    const params = {
      page: pagination?.current || tableParams.pagination.current,
      perpage: pagination?.pageSize || tableParams.pagination.pageSize,
      ...(sorter?.order && {
        sort: `${sorter.order === "descend" ? "-" : ""}${sorter.field}`,
      }),
    };

    try {
      const { types, total, page, perPage } = await getList(params);
      setData(types);

      const updatedTableParams = {
        pagination: {
          ...tableParams.pagination,
          total,
          current: page,
          pageSize: perPage,
        },
      };
      setTableParams(updatedTableParams);

      // Сохраняем параметры пагинации в localStorage
      localStorage.setItem(
        `${user.user_uuid}_${LOCAL_STORAGE_KEY}`,
        JSON.stringify(updatedTableParams)
      );
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  //  initial fetch
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  const handleTableChange = async (pagination, filters, sorter) => {
    fetchData(pagination, sorter);
  };

  const handlePaginationChange = (current, pageSize) => {
    // При изменении pageSize сбрасываем страницу на 1
    const shouldResetPage = pageSize !== tableParams.pagination.pageSize;

    const newPagination = {
      ...tableParams.pagination,
      current: shouldResetPage ? 1 : current,
      pageSize,
    };

    fetchData(newPagination, tableParams.sorter);
  };

  // Обработчик добавления нового типа РГ
  const handleAddNew = () => {
    setCurrentItem({ idx: data.length });
    setIsModalShow(true);
  };

  // Обработчик редактирования типа РГ
  const handleEditItem = (item) => {
    setCurrentItem(item);
    setIsModalShow(true);
  };

  // Обработчик сохранения типа РГ
  const handleSaveItem = () => {
    setIsModalShow(false);
    if (currentItem) {
      fetchData();
    } else {
      fetchData({ current: 1 });
    }
  };

  // Обработчик удаления типа РГ
  const handleDeleteItem = (item) => {
    Modal.confirm({
      title: item.can_be_marked_deleted
        ? `Отметить тип ${item.name} как удаленный?`
        : `Вы уверены, что хотите удалить тип ${item.name}?`,
      content: item.can_be_marked_deleted
        ? "Этот тип будет отмечен как удаленный, но не будет удален из системы полностью, т.к. он используется в удаленных рабочих группах."
        : "Это приведет к его полному удалению из системы.",
      onOk() {
        handleConfirmDelete(item);
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  // Действительно удаляем тип после подтверждения
  const handleConfirmDelete = async (item) => {
    setIsLoading(true);
    try {
      await deleteItem({ uuid: item.uuid });
      await fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setIsLoading(false);
    }
  };

  return (
    <>
      <Flex vertical gap={25} className="h-100">
        <Flex vertical gap={10}>
          <Breadcrumbs
            items={[
              {
                title: "Административная панель",
              },
              {
                title: "Системные справочники",
              },
              {
                title: "Типы рабочих групп",
              },
            ]}
          />
          <Flex gap={20} align="center" justify="space-between">
            <Title level={2} className="no-margin">
              Типы рабочих групп
            </Title>

            <Button
              type="primary"
              icon={<PlusIcon />}
              onClick={handleAddNew}
            ></Button>
          </Flex>

          <Text style={{ maxWidth: 820, lineHeight: 2 }}>
            Типы рабочих групп, помеченные как&nbsp;
            <Tag variant="outlined" style={{ marginRight: 0 }}>
              Основной тип
            </Tag>
            , зарезервированы и&nbsp;не могут быть изменены или удалены. Рабочие
            группы с&nbsp;типом, у&nbsp;которого отмечен флаг&nbsp;
            <Tag variant="outlined" style={{ marginRight: 0 }}>
              Участвует в оргструктуре
            </Tag>
            , будут учитываться при&nbsp;построении структуры организации.
          </Text>
        </Flex>

        <div ref={tableContainerRef} style={{ flex: 1, minHeight: 0 }}>
          <UnitTypesTable
            data={data}
            loading={isLoading}
            pagination={false}
            onChange={handleTableChange}
            onEditItem={handleEditItem}
            onDeleteItem={handleDeleteItem}
            scroll={{ x: "100%", y: tableHeight - headerHeight }}
          />
        </div>

        <div style={{ minHeight: 32 }}>
          <Pagination
            {...tableParams.pagination}
            onChange={handlePaginationChange}
          />
        </div>
      </Flex>

      {isModalShow && (
        <UnitTypeModal
          data={currentItem}
          visible={isModalShow}
          onSave={handleSaveItem}
          onCancel={() => setIsModalShow(false)}
        />
      )}
    </>
  );
};

export default UnitTypes;
